import { useEffect, useMemo, useRef, useState } from "react";

function CategoryMultiSelect({
  options = [],
  value = [],
  onChange,
  loading = false,
  emptyPlaceholder = "All categories",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(o.id)),
    [options, selectedSet]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.name?.toLowerCase().includes(q) || o.tag?.toLowerCase().includes(q)
    );
  }, [options, search]);

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const removeChip = (e, id) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        onClick={() => setOpen((o) => !o)}
        className="form-input w-full min-h-[42px] flex flex-wrap gap-1.5 items-center cursor-pointer"
      >
        {selectedOptions.length === 0 ? (
          <span className="text-slate-400 dark:text-slate-500">
            {emptyPlaceholder}
          </span>
        ) : (
          selectedOptions.map((o) => (
            <span
              key={o.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400"
            >
              {o.name}
              <button
                type="button"
                onClick={(e) => removeChip(e, o.id)}
                className="hover:text-indigo-800 dark:hover:text-indigo-200"
              >
                &times;
              </button>
            </span>
          ))
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg p-2">
          <input
            type="text"
            autoFocus
            placeholder="Search categories by name or tag..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full text-sm mb-2"
          />
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <p className="text-xs text-slate-500 text-center py-3">Loading categories...</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-3">No categories found</p>
            ) : (
              filtered.map((o) => {
                const isSelected = selectedSet.has(o.id);
                return (
                  <div
                    key={o.id}
                    onClick={() => toggle(o.id)}
                    className={`p-2 text-sm cursor-pointer rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${
                      isSelected ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox pointer-events-none"
                      checked={isSelected}
                      readOnly
                    />
                    <span className="flex-1 text-slate-800 dark:text-slate-100">{o.name}</span>
                    {o.tag && (
                      <span className="text-xs text-slate-400">{o.tag}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryMultiSelect;
